import {RecyclerScrollView, IScrollConfig, IScrollElementFactory} from "./RecyclerScrollView";
import InteractionEvent = PIXI.interaction.InteractionEvent;

export interface ISortingScrollElementFactory<T> {
    instance(): ISortingScrollElement<T>;
}

export interface ISortingScrollElement<T> extends PIXI.Container {
    id:number;
    setData(data: T): void;
    updatePositions(): void;
}

export class SortingAnimationRecyclerScrollView<T> extends RecyclerScrollView<T> {
    public isAnimating: boolean = false;
    protected _animations: any[] = [];

    protected _elementFactory: ISortingScrollElementFactory<T>;

    /* used for animation purposes only */
    protected _firstChildY: number = null;
    protected _additionalElements: ISortingScrollElement<T>[];
    protected _globalStartPos: PIXI.Point;
    protected _oldDataIds: any[] = [];
    constructor(elementFactory: IScrollElementFactory<T>, maxElementsCap: number, config: IScrollConfig) {
        super(elementFactory,maxElementsCap,config);
    }

    init() {
        const { placeholderTextureID, scrollBarConfig } = this.conf;
        this.interactive = true;
        this.name = "scroll";
        this._firstElementIndex = 0;
        this._dataArray = [];

        this.createMask();

        placeholderTextureID && this.createPlaceHolders();
        this.createAdditionalElements();
        this.createContainer();
        this.createElements();
        this.setMask();
        this.createHitArea();
        scrollBarConfig && this.createScrollBar();
        this.setListeners();

        PIXI.ticker.shared.add(this.animateScroll, this);
    }

    get additionalElements(): ISortingScrollElement<T>[] {
        return this._additionalElements;
    }

    get elements(): ISortingScrollElement<T>[] {
        return this._container.children as ISortingScrollElement<T>[];
    }

    public setData(
        dataArray: Array<any>,
        returnToTop?: boolean,
        preventAnimation: boolean = false
    ) {
        const {placeholderTextureID,scrollBarConfig} = this.conf
        if (
            dataArray.length <= this.getMaximumVisibleElements() ||
            dataArray.length < this._dataArray.length) {
            returnToTop = true;
        }

        if (this.isMoving || this._isPointerDown) {
            this._isPointerDown = false;
            this._scrollSpeed = 0;
            this.isMoving = false;
        }

        if (this.isAnimating) {
            gsap.TweenMax.killDelayedCallsTo(this.completeAnimation);
            this.completeAnimation();
        }

        this._oldDataIds = this._dataArray.length
            ? [...this._dataArray]
                  .map((d:any) => d.id)
                  .slice(
                      this._firstElementIndex,
                      this._firstElementIndex + this.elements.length
                  )
            : [];

        this._dataArray = [...dataArray];

        if (returnToTop) this.repositionElements(true);

        placeholderTextureID && this.setPlaceHolderVisibility();
        scrollBarConfig && this.setScrollbar();

        if (preventAnimation) this.completeAnimation();
        else this.animateCurrentTickets();
    }

    public clear(): void {
        const {placeholderTextureID,scrollBarConfig} = this.conf
        this._oldDataIds = [];
        this._dataArray = [];
        this.hideAdditionalElements();
        this.elements.forEach((el: any) => {
            el.alpha = 0;
            el.id = null;
        });
        scrollBarConfig && this.setScrollbar();
        placeholderTextureID && this.setPlaceHolderVisibility();
    }

    public setPosition(position: PIXI.Point) {
        if (this.isAnimating) {
            gsap.TweenMax.killDelayedCallsTo(this.completeAnimation);
            this.completeAnimation();
        }
        super.setPosition(position);
    }

    public update(returnToTop: boolean = false) {
        if (this.isAnimating) {
            gsap.TweenMax.killDelayedCallsTo(this.completeAnimation);
            this.completeAnimation();
        }
        super.update(returnToTop)
    }

    protected animateCurrentTickets() {
        this.isAnimating = true;
        //position from which to start repositioning tickets to prevent displacements
        this._globalStartPos = this.elements[0].getGlobalPosition();
        this._firstChildY = this.elements[0].position.clone().y;

        this.elements.forEach((el) => {
            const verticalSpace = this.conf.elementsVerticalOffset
            const id = el.id;
            const positionByIndexInContainer = this._dataArray
                .slice(
                    this._firstElementIndex,
                    this._firstElementIndex + this.elements.length
                )
                .map((e: any) => e.id)
                .indexOf(id);
            const ticketData = this._dataArray.find((e: any) => e.id == id);

            if (ticketData && id !== null && id !== undefined) {
                if (positionByIndexInContainer != -1) {
                    const newPosition = new PIXI.Point();
                    //set new matched number before the animation starts
                    el.setData(ticketData)

                    newPosition.y =
                        this._firstChildY +
                        positionByIndexInContainer *
                            (el.height + verticalSpace);

                    const t = gsap.TweenMax.to(el, 0.38, {
                        y: newPosition.y,
                    });
                    this._animations.push(t);
                } else {
                    const indexInDataArray = this._dataArray
                        .map((e: any) => e.id)
                        .indexOf(id);
                    const direction =
                        indexInDataArray < this._firstElementIndex ? "-" : "+";
                    const t = gsap.TweenMax.to(el, 0.38, {
                        y: `${direction}=100`,
                        alpha: 0,
                    });
                    this._animations.push(t);
                }
            }
        });

        gsap.TweenMax.delayedCall(0.1, this.animateNewTicketsFadeIn, [], this);
    }

    protected animateNewTicketsFadeIn(): void {
        const verticalSpace = this.conf.elementsVerticalOffset
        const tm = new gsap.TimelineMax();
        const startPos = this.toLocal(this._globalStartPos);
        const currentData = this._dataArray.slice(
            this._firstElementIndex,
            this._firstElementIndex + this.elements.length
        );

        currentData.forEach((data: any, index: number) => {
            const isNew = this._oldDataIds.indexOf(data.id) === -1;
            if (isNew) {
                const el = this._additionalElements[index] as any;
                el.x = startPos.x;
                el.y = startPos.y + index * (el.height + verticalSpace);
                el.setData(data)
                tm.add(gsap.TweenMax.to(el, 0.36, { alpha: 1 }), `-=0.32`);
            }
        });

        this._animations.push(tm);
        tm.play();
        gsap.TweenMax.delayedCall(0.38, this.completeAnimation, [], this);
    }

    public completeAnimation(): void {
        const elementsVerticalOffset = this.conf.elementsVerticalOffset
        if (this.isAnimating) this.killAnimations();

        let startY;
        if (this._firstChildY === undefined || this._firstChildY === null)
            startY = this.elements[0].position.clone().y;
        else startY = this._firstChildY;

        this.elements.forEach((el: any, index) => {
            el.y = startY + index * (el.height + elementsVerticalOffset)
            this.populateElement(index, this._firstElementIndex + index)
        });

        this._firstChildY = null;
        this._globalStartPos = null;

        this.reorderAndOutOfBoundsCheck();
    }

    protected reorderAndOutOfBoundsCheck() {
        if (this.isAnimating) return;
        super.reorderAndOutOfBoundsCheck();
    }

    protected createAdditionalElements(): void {
        this._additionalElements = [];
        for (
            let i = 0;
            i <
            this._maxElementsCap + RecyclerScrollView.NUMBER_OF_ADDITIONAL_ELEMENTS;
            i++
        ) {
            const el = this._elementFactory.instance();
            el.alpha = 0;
            this._additionalElements.push(el);
            this.addChild(el);
        }
    }

    protected hideAdditionalElements(): void {
        this._additionalElements.forEach((el: any) => {
            el.alpha = 0;
            el.interactive = false;
            el.id = null;
            el.position.set(0, 0);
        });
    }

    protected onPointerDown(e: InteractionEvent): void {
        if (this.isAnimating) {
            gsap.TweenMax.killDelayedCallsTo(this.completeAnimation);
            this.completeAnimation();
        }
        super.onPointerDown(e);
    }

    protected onPointerMove(e: PIXI.interaction.InteractionEvent) {
        if (this.isAnimating) return;
        super.onPointerMove(e);
    }

    protected animateScroll() {
        if (this.isAnimating) return;
        super.animateScroll();
    }

    protected onWheelScroll(e: WheelEvent): void {
        if (this.isAnimating) {
            gsap.TweenMax.killDelayedCallsTo(this.completeAnimation);
            this.completeAnimation();
        }
        super.onWheelScroll(e);
    }

    public killAnimations(): void {
        gsap.TweenMax.killDelayedCallsTo(this.animateNewTicketsFadeIn);
        this._animations.forEach((tw) => {
            tw.isActive() && tw.kill();
        });
        this.hideAdditionalElements();
        this.isAnimating = false;
        this._animations = [];
    }
}
